import { FileProcessingStatusType, type DocumentFileType } from 'app/src/model'
import { documentAiApi, type portalConfigurationApi } from 'app/src/services/welfareportal'
import { InformativeBanner } from 'app/src/shared/components'
import { downloadBlob, useHttpClient, useWebAppContext } from 'app/src/shared/hooks'
import { cloneDeep } from 'lodash'
import prettyBytes from 'pretty-bytes'
import { Button } from 'primereact/button'
import { Divider } from 'primereact/divider'
import { FileUpload, FileUpload as PrimeFileUpload, type FileUploadSelectEvent } from 'primereact/fileupload'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function RefundFileUploader(props: {
  files: DocumentFileType[]
  analyzeBlocking?: boolean
  documentAttributes: {
    documentExtractionType: string
    documentScope: string
    min: number
    max: number
  }
  onFileLoaded: (file: DocumentFileType) => void
  onDeletedFile: (documentId: string) => void
  refundId: string
}) {
  const context = useWebAppContext()
  const { t } = useTranslation('shopping', { keyPrefix: 'Refund' })
  const shoppingConfig: Required<portalConfigurationApi.ShoppingConfig['documentAi']> = useMemo(() => {
    const def_PollingInterval = 1000
    const def_MaxCount = 10
    const def_MaxUploadSize = 5000000
    const def_FileAcceptMimeTypes = ['application/pdf', 'image/png', 'image/jpeg']
    const def_FileAcceptTypes = ['.pdf', '.png', '.jpg']
    if (!context.portalConfiguration.shoppingConfig?.documentAi)
      return {
        documentAiPollingInterval: def_PollingInterval,
        documentAiMaxCount: def_MaxCount,
        maxUploadFileSize: def_MaxUploadSize,
        fileAcceptMimeTypes: def_FileAcceptMimeTypes,
        fileAcceptTypes: def_FileAcceptTypes,
      }
    else
      return {
        documentAiPollingInterval:
          context.portalConfiguration.shoppingConfig.documentAi.documentAiPollingInterval ?? def_PollingInterval,
        documentAiMaxCount: context.portalConfiguration.shoppingConfig.documentAi.documentAiMaxCount ?? def_MaxCount,
        maxUploadFileSize: context.portalConfiguration.shoppingConfig.documentAi.maxUploadFileSize ?? def_MaxUploadSize,
        fileAcceptMimeTypes: context.portalConfiguration.shoppingConfig.documentAi.fileAcceptMimeTypes ?? def_FileAcceptMimeTypes,
        fileAcceptTypes: context.portalConfiguration.shoppingConfig.documentAi.fileAcceptTypes ?? def_FileAcceptTypes,
      }
  }, [context.portalConfiguration.shoppingConfig])

  const fileUploadRef = useRef<FileUpload>(null)

  const httpClient = useHttpClient()
  const [currentUpload, setCurrentUpload] = useState<DocumentFileType>()
  const [currentBlob, setCurrentBlob] = useState<Blob>()

  const createDocumentMutation = documentAiApi.useDocumentCreate()
  const deleteDocumentMutation = documentAiApi.useDocumentDelete()

  const createDocument = (currUpload: DocumentFileType) => {
    return createDocumentMutation.mutateAsync({
      body: {
        entityId: props.refundId,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mimeType: currUpload.documentMimeType as any,
        name: currUpload.documentName,
        scope: props.documentAttributes?.documentScope,
        size: currUpload.documentSize ?? 0,
        type: props.documentAttributes?.documentExtractionType,
      },
    })
  }

  const subscribeProcessingStatus = useCallback(
    async (curUpload_: DocumentFileType, currentRetry: number = 0) => {
      let processingOutDocument: documentAiApi.Document | undefined = undefined

      try {
        if (!curUpload_.analyzeOperationId) {
          setCurrentUpload({ ...curUpload_, status: FileProcessingStatusType.PROCESSED })
          return
        } else {
          processingOutDocument = await documentAiApi.fetchAsyncAnalyzedResult({
            pathParams: { documentId: curUpload_.documentId!, operationId: curUpload_.analyzeOperationId },
          })
        }
      } finally {
        // eslint-disable-next-line no-unsafe-finally
        if (!curUpload_.analyzeOperationId) return
        if (
          processingOutDocument?.documentInfo?.status === 'analyze_failed' ||
          processingOutDocument?.documentInfo?.status === 'analyzed'
        ) {
          setCurrentUpload({ ...curUpload_, documentDetails: processingOutDocument, status: FileProcessingStatusType.PROCESSED })
        } else {
          if (
            (processingOutDocument?.documentInfo?.status === 'analyzing_model' ||
              processingOutDocument?.documentInfo?.status === 'analyzing_text' ||
              !processingOutDocument) &&
            currentRetry <= shoppingConfig.documentAiMaxCount
          ) {
            await new Promise((resolve) => setTimeout(resolve, shoppingConfig.documentAiPollingInterval))
            await subscribeProcessingStatus(curUpload_, currentRetry + 1)
          } else
            setCurrentUpload({
              ...curUpload_,
              documentDetails: processingOutDocument,
              errorOccurred: false,
              status: FileProcessingStatusType.PROCESSED_TIMEOUT,
            })
        }
      }
    },
    [shoppingConfig],
  )

  const handleUpload = useCallback(
    async (curUpdate: DocumentFileType) => {
      let status = curUpdate?.status
      let errorOccurred = false
      let documentDetails: documentAiApi.Document | undefined = undefined
      let analyzeOperationStatus: documentAiApi.OperationStatus | undefined = undefined
      let analyzeOperationId: string | undefined = undefined
      try {
        const response = await httpClient.uploadFile(curUpdate.documentUri!, curUpdate.blob!)
        if (response && !response.fetchError && response.httpResponse?.ok) status = FileProcessingStatusType.UPLOADED
        else throw new Error()

        const beginAnalyzeResponse = await documentAiApi.fetchBeginDocumentAnalyze({
          pathParams: { documentId: curUpdate.documentId! },
        })
        if (beginAnalyzeResponse.status == 'ok' && beginAnalyzeResponse.document) documentDetails = beginAnalyzeResponse.document

        analyzeOperationStatus = beginAnalyzeResponse.status
        analyzeOperationId = beginAnalyzeResponse.operationId
        status = FileProcessingStatusType.UPLOAD_NOTIFIED
      } catch {
        errorOccurred = true
      } finally {
        setCurrentUpload({ ...curUpdate, status, errorOccurred, documentDetails, analyzeOperationStatus, analyzeOperationId })
      }
    },
    [httpClient],
  )

  useEffect(() => {
    if (currentUpload && !currentUpload.errorOccurred) {
      switch (currentUpload.status) {
        case FileProcessingStatusType.DOCUMENTID_ASSIGNED:
          if (currentUpload?.blob) handleUpload(currentUpload)
          break
        case FileProcessingStatusType.UPLOAD_NOTIFIED:
          subscribeProcessingStatus(currentUpload)
          break
        case FileProcessingStatusType.PROCESSED:
        case FileProcessingStatusType.PROCESSED_TIMEOUT:
          if (
            typeof props.analyzeBlocking !== 'undefined' &&
            props.analyzeBlocking &&
            currentUpload?.analyzeOperationId &&
            (currentUpload?.status == FileProcessingStatusType?.PROCESSED_TIMEOUT ||
              currentUpload?.documentDetails?.documentInfo?.status === 'analyze_failed')
          ) {
            if (currentUpload?.documentDetails?.documentInfo?.status === 'analyze_failed') {
              context.showToastMessage({ severity: 'error', content: t('Errors.Uploader.documentAiAnalyzeFailedMessage') })
              context.logger?.error('Error Analyzing Document', {
                analyzeOperationId: currentUpload?.analyzeOperationId,
                documentId: currentUpload?.documentId,
              })
            } else {
              context.showToastMessage({ severity: 'error', content: t('Errors.Uploader.documentAiTimeOutMessage') })
              context.logger?.error('Analyzing Document Timeout', {
                analyzeOperationId: currentUpload?.analyzeOperationId,
                documentId: currentUpload?.documentId,
              })
            }
          } else {
            props.onFileLoaded(currentUpload)
            setCurrentUpload(undefined)
          }

          break
        default:
          break
      }
    } else {
      if (currentUpload) {
        context.showToastMessage({ content: t('Errors.Uploader.uploadGenericError'), severity: 'error' })
        setCurrentUpload(undefined)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUpload])

  useEffect(() => {
    if (currentBlob && currentUpload) {
      setCurrentUpload({ ...currentUpload, blob: currentBlob })
      setCurrentBlob(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBlob])

  const files =
    currentUpload &&
    currentUpload.status > FileProcessingStatusType.CREATED &&
    currentUpload.status !== FileProcessingStatusType.PROCESSED
      ? [...props.files, currentUpload]
      : [...props.files]

  const handleOnFileChange = (file: File) => {
    if (file) {
      if (file.size > shoppingConfig.maxUploadFileSize) {
        context.showToastMessage({ content: t('Errors.Uploader.uploadMaxSizeError'), severity: 'error' })
        return
      }
      if (!shoppingConfig.fileAcceptMimeTypes?.some((x) => x.trim() === file.type)) {
        context.showToastMessage({ content: t('Errors.Uploader.uploadFileTypeError'), severity: 'error' })
        return
      }

      const currUpload: DocumentFileType = {
        documentName: file.name,
        documentSize: file.size,
        documentMimeType: file.type,
        documentType: props.documentAttributes?.documentExtractionType,
        status: FileProcessingStatusType.CREATED,
      }

      setCurrentUpload(currUpload)
      const fileReader = new FileReader()

      fileReader.onload = function () {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result: any = fileReader.result
        setCurrentBlob(new Blob([result], { type: file.type }))
        fileUploadRef?.current?.clear()
      }

      createDocument(currUpload)
        .then((res) => {
          currUpload.documentId = res.documentId
          currUpload.documentUri = res.documentUrl
          currUpload.status = FileProcessingStatusType.DOCUMENTID_ASSIGNED
          setCurrentUpload(currUpload)
          fileReader.readAsArrayBuffer(file)
        })
        .catch(() =>
          setCurrentUpload({
            ...currUpload,
            errorOccurred: true,
          }),
        )
    }
  }

  const handleFileDelete = async (documentId: string) => {
    try {
      await deleteDocumentMutation.mutateAsync({ pathParams: { documentId } })
      props.onDeletedFile(documentId)
    } catch (error) {
      context.logger?.error('Error deleting Document', { error })
      context.showToastMessage({ content: t('Errors.Uploader.deletingGenericError'), severity: 'error' })
    }
  }

  const isPending = (file: DocumentFileType | undefined) => {
    if (!file) return false
    else return file.status < FileProcessingStatusType.PROCESSED && !file.errorOccurred
  }

  return (
    <div>
      <div className='mx-auto flex flex-wrap justify-center gap-2'>
        <div>
          <PrimeFileUpload
            mode='basic'
            name='files[]'
            accept={shoppingConfig.fileAcceptMimeTypes.join(',')}
            pt={{
              input: {
                'data-testid': 'file-upload-input',
              },
              root: {
                className: 'flex justify-center my-4',
              },
            }}
            maxFileSize={shoppingConfig.maxUploadFileSize}
            chooseLabel={t('Errors.Uploader.uploadDocumentsButtonLabel')}
            chooseOptions={{ icon: 'pi pi-upload' }}
            ref={fileUploadRef}
            disabled={props.documentAttributes?.max === props.files?.length || isPending(currentUpload)}
            onSelect={(evt: FileUploadSelectEvent) => handleOnFileChange(evt.files[0])}
          />
        </div>
        <div className='flex items-center justify-center'>
          <InformativeBanner
            inlineIcon
            text={t('Errors.Uploader.supportedFileTypeAndMaxSize', {
              SUPPORTEDFILETYPES: shoppingConfig.fileAcceptTypes?.join(','),
              MAXFILESIZE: prettyBytes(shoppingConfig.maxUploadFileSize),
            })}
          />
        </div>
      </div>
      {files && files.length > 0 && (
        <>
          <div className='my-4 text-center'>
            <div>
              <Divider pt={{ root: { className: 'border-[var(--primary-color)]' } }} />

              <p>{t('Errors.Uploader.fileTableTitle')}</p>
            </div>
          </div>

          <div className='flex justify-center'>
            <div>
              {files.map((file: DocumentFileType) => (
                <div key={file.documentId} className='mt-2 grid grid-cols-1 md:grid-cols-5'>
                  <div className='col-span-1 my-auto line-clamp-1 text-ellipsis text-left md:col-span-3'>
                    <Button
                      label={file.documentName}
                      text
                      className='mt-[5px] pl-[10px] text-left'
                      icon='pi pi-file'
                      iconPos='left'
                      pt={{
                        label: { className: 'text-[16px] text-[#323130] underline font-normal leading-[22px]' },
                        icon: { className: 'text-[16px]' },
                      }}
                      onClick={() => {
                        if (file?.blob) downloadBlob(file.blob, file?.documentName)
                      }}
                    />
                  </div>
                  <div className='col-span-1 my-auto text-center md:col-span-1'>
                    <p className='pt-1 underline'>{prettyBytes(file.documentSize!)}</p>
                  </div>
                  <div className='col-span-1 my-auto text-center md:col-span-1'>
                    {isPending(file) ? (
                      <ProgressSpinner
                        data-testid='loading-element'
                        pt={{
                          root: {
                            className: 'w-[50px] h-[50px]',
                          },
                        }}
                      />
                    ) : (
                      <>
                        {(file.status === FileProcessingStatusType.PROCESSED ||
                          (file.status === FileProcessingStatusType.PROCESSED_TIMEOUT &&
                            (typeof props.analyzeBlocking === 'undefined' || !props.analyzeBlocking))) && (
                          <Button
                            icon='pi-trash pi'
                            outlined
                            severity='danger'
                            aria-label={t('Errors.Uploader.deleteFileLabel')}
                            tooltip={t('Errors.Uploader.deleteFileLabel')}
                            onClick={() => handleFileDelete(file.documentId!)}
                          />
                        )}
                        {file.status === FileProcessingStatusType.PROCESSED_TIMEOUT &&
                          typeof props.analyzeBlocking !== 'undefined' &&
                          props.analyzeBlocking && (
                            <Button
                              icon='pi-refresh pi'
                              aria-label={t('Errors.Uploader.retryFileLabel')}
                              severity='danger'
                              outlined
                              pt={{
                                icon: {
                                  className: 'pr-2',
                                },
                              }}
                              onClick={() => {
                                const currUpload_: DocumentFileType | undefined = cloneDeep(currentUpload)
                                currUpload_!.status = FileProcessingStatusType.UPLOAD_NOTIFIED
                                setCurrentUpload(currUpload_)
                              }}
                            >
                              {t('Errors.Uploader.retryFileLabel')}
                            </Button>
                          )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
